import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import Badge from '../../components/ui/Badge';
import { Button } from '../../components/ui';
import { AccordionItem } from '../../components/ui/Accordion';
import { RefreshIcon, ClockIcon } from '../../components/ui/Icons';
import { useNotification } from '../../contexts/NotificationContext';
import { generateCrewAiOverview, getCrewAiOverview, getCrewAiOverviewHistory, type CrewAiOverviewResponse } from '../../services/mutations';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { moduleConfig } from './config';
import type { CrewCertRow, CrewFile, CrewTransactionRow } from './types';

const formatDate = (value?: string | null) => (value ? formatDateGlobal(value) : '—');

const formatGeneratedAt = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const readinessVariant = (readiness: CrewAiOverviewResponse['overview']['readiness']) => {
  switch (readiness) {
    case 'READY':
      return 'success' as const;
    case 'CONDITIONAL':
      return 'warning' as const;
    case 'NOT_READY':
      return 'danger' as const;
    default:
      return 'secondary' as const;
  }
};

const AiOverviewBody: React.FC<{ data: CrewAiOverviewResponse; compactMeta?: boolean }> = ({
  data,
  compactMeta = false,
}) => {
  const { colors } = useTheme();
  const { overview } = data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="min-w-0">
          <p className={cn('text-sm font-semibold', colors.text)}>{overview.headline}</p>
          <p className={cn('mt-1.5 text-sm leading-relaxed', colors.textSecondary)}>{overview.summary}</p>
        </div>
        <Badge variant={readinessVariant(overview.readiness)}>{overview.readiness.replace(/_/g, ' ')}</Badge>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {(
          [
            ['Strengths', overview.strengths],
            ['Risks', overview.risks],
            ['Recommendations', overview.recommendations],
          ] as const
        ).map(([title, items]) => (
          <div key={title}>
            <p className={cn('text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}>{title}</p>
            {items.length ? (
              <ul className={cn('space-y-1.5 text-sm list-disc pl-4', colors.text)}>
                {items.map((itemText) => (
                  <li key={itemText}>{itemText}</li>
                ))}
              </ul>
            ) : (
              <p className={cn('text-sm', colors.textMuted)}>—</p>
            )}
          </div>
        ))}
      </div>
      {!compactMeta ? (
        <p className={cn('text-xs', colors.textMuted)}>
          Generated {formatGeneratedAt(data.generatedAt)} · {data.model}
        </p>
      ) : null}
    </div>
  );
};
const formatYears = (value?: number | null) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const formatMonths = (value?: number | null) => {
  if (value == null || !Number.isFinite(value)) return '—';
  return String(Math.round(value));
};

const formatWage = (tx?: CrewTransactionRow | null) => {
  if (tx?.totalWage == null) return null;
  const amount = typeof tx.totalWage === 'number' ? tx.totalWage : Number(tx.totalWage);
  if (!Number.isFinite(amount)) return null;
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return tx.wageCurrency ? `${formatted} ${tx.wageCurrency}` : formatted;
};

const ageFromBirthDate = (birthDate?: string | null) => {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let years = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years -= 1;
  return years >= 0 ? years : null;
};

const daysUntil = (iso?: string | null) => {
  if (!iso) return null;
  const end = new Date(iso);
  if (Number.isNaN(end.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - today.getTime()) / 86_400_000);
};

const daysHint = (days: number | null | undefined, overdueWord = 'overdue') => {
  if (days == null) return undefined;
  if (days < 0) return `${Math.abs(days)}d ${overdueWord}`;
  return `${days}d left`;
};

const getCurrentAssignment = (transactions?: CrewTransactionRow[]) => {
  const list = transactions ?? [];
  const onBoard = list.filter((t) => t.status === 'ONB');
  if (onBoard.length) {
    return onBoard.sort((a, b) => (b.signOnDate ?? '').localeCompare(a.signOnDate ?? ''))[0];
  }
  return list.sort((a, b) =>
    (b.signOnDate ?? b.startDate ?? '').localeCompare(a.signOnDate ?? a.startDate ?? ''),
  )[0];
};

const certStatus = (expiredDate?: string | null) => {
  const days = daysUntil(expiredDate);
  if (days == null) return { label: 'No expiry', variant: 'secondary' as const, tone: 'muted' as const };
  if (days < 0) return { label: 'Expired', variant: 'danger' as const, tone: 'danger' as const };
  if (days <= 90) return { label: `Expires in ${days}d`, variant: 'warning' as const, tone: 'warning' as const };
  return { label: formatDate(expiredDate), variant: 'success' as const, tone: 'ok' as const };
};

type MetricTone = 'default' | 'danger' | 'warning' | 'ok';

type TopMetric = {
  id: string;
  label: string;
  value: string;
  hint?: string;
  tone?: MetricTone;
};

const toneValueClass = (tone: MetricTone, isDark: boolean): string => {
  if (tone === 'danger') return isDark ? 'text-red-400' : 'text-red-600';
  if (tone === 'warning') return isDark ? 'text-amber-400' : 'text-amber-600';
  if (tone === 'ok') return isDark ? 'text-emerald-400' : 'text-emerald-700';
  return '';
};

const MetricCell: React.FC<Omit<TopMetric, 'id'>> = ({ label, value, hint, tone = 'default' }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="min-w-0 px-3 py-3 sm:px-4">
      <p className={cn('text-xs font-semibold uppercase tracking-wide leading-snug', colors.textMuted)}>
        {label}
      </p>
      <p
        className={cn(
          'mt-1.5 text-sm font-semibold tabular-nums truncate',
          tone === 'default' ? colors.text : toneValueClass(tone, isDark),
        )}
        title={value}
      >
        {value}
      </p>
      {hint ? (
        <p className={cn('mt-0.5 text-xs truncate', colors.textMuted)} title={hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const MetricsGroup: React.FC<{
  title: string;
  metrics: TopMetric[];
  columnsClass?: string;
}> = ({ title, metrics, columnsClass = 'grid-cols-2 md:grid-cols-3 xl:grid-cols-6' }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border shadow-sm overflow-hidden', colors.surface, colors.border)}>
      <div className={cn('border-b px-4 py-2', colors.border, colors.headerBg)}>
        <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>{title}</h3>
      </div>
      <div className={cn('grid divide-x divide-y xl:divide-y-0', columnsClass, colors.border)}>
        {metrics.map(({ id, label, value, hint, tone }) => (
          <MetricCell key={id} label={label} value={value} hint={hint} tone={tone} />
        ))}
      </div>
    </section>
  );
};

type InfoRowProps = { label: string; value?: React.ReactNode };

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => {
  const { colors } = useTheme();
  const empty = value == null || value === '' || value === '—';
  return (
    <div className={cn('grid grid-cols-[minmax(6.5rem,36%)_1fr] gap-x-3 py-1.5 border-b border-dashed last:border-0', colors.border)}>
      <dt className={cn('text-xs font-medium', colors.textMuted)}>{label}</dt>
      <dd className={cn('text-sm min-w-0 break-words', empty ? colors.textMuted : colors.text)}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
};

type PanelProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
};

const Panel: React.FC<PanelProps> = ({ title, subtitle, action, children, className }) => {
  const { colors } = useTheme();
  return (
    <section
      className={cn(
        'rounded-xl border shadow-sm overflow-hidden flex flex-col min-h-0',
        colors.surface,
        colors.border,
        className,
      )}
    >
      <div
        className={cn(
          'flex items-start justify-between gap-3 border-b px-4 py-3',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="min-w-0">
          <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
          {subtitle ? <p className={cn('text-xs mt-0.5', colors.textMuted)}>{subtitle}</p> : null}
        </div>
        {action}
      </div>
      <div className="p-4 flex-1 min-h-0">{children}</div>
    </section>
  );
};

const EXPERIENCE_METRICS: Array<{
  key: keyof NonNullable<CrewFile['experience']>;
  label: string;
  format: (v: number) => string;
}> = [
  { key: 'timeInRankYears', label: 'Time in rank', format: formatYears },
  { key: 'timeWithCompanyYears', label: 'With company', format: formatYears },
  { key: 'timeOnShipTypeYears', label: 'Ship type', format: formatYears },
  { key: 'timeWithMainEngineYears', label: 'Main engine', format: formatYears },
  { key: 'timeOnOtherShipTypesYears', label: 'Other ship types', format: formatYears },
  { key: 'timeOnboardThisTourMonths', label: 'This tour (mo)', format: formatMonths },
];

export type CrewDashboardProps = {
  item: CrewFile;
  /** Live preview while editing — hides persistent actions */
  preview?: boolean;
};

export const CrewDashboard: React.FC<CrewDashboardProps> = ({
  item,
  preview = false,
}) => {
  const { colors } = useTheme();
  const { showError } = useNotification();
  const [aiOverview, setAiOverview] = useState<CrewAiOverviewResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHydrating, setAiHydrating] = useState(!preview && item.id > 0);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<CrewAiOverviewResponse[]>([]);
  const [historySelectedId, setHistorySelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (preview || item.id <= 0) {
      setAiOverview(null);
      setAiHydrating(false);
      return;
    }

    let cancelled = false;
    setAiOverview(null);
    setAiHydrating(true);
    getCrewAiOverview(item.id)
      .then((saved) => {
        if (!cancelled) {
          setAiOverview(saved);
          if (saved) setAiPanelOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setAiOverview(null);
      })
      .finally(() => {
        if (!cancelled) setAiHydrating(false);
      });

    return () => {
      cancelled = true;
    };
  }, [item.id, preview]);
  const current = useMemo(() => getCurrentAssignment(item.transactions), [item.transactions]);

  const certStats = useMemo(() => {
    const certs = item.certs ?? [];
    const expiring = certs.filter((c) => {
      const d = daysUntil(c.expiredDate);
      return d != null && d >= 0 && d <= 90;
    }).length;
    const expired = certs.filter((c) => {
      const d = daysUntil(c.expiredDate);
      return d != null && d < 0;
    }).length;
    return { total: certs.length, expiring, expired };
  }, [item.certs]);

  const sortedCerts = useMemo(
    () =>
      [...(item.certs ?? [])].sort((a, b) => {
        const da = daysUntil(a.expiredDate) ?? 9999;
        const db = daysUntil(b.expiredDate) ?? 9999;
        return da - db;
      }),
    [item.certs],
  );

  const recentAppraisals = useMemo(
    () =>
      [...(item.appraisals ?? [])]
        .sort((a, b) => (b.assessmentDate ?? '').localeCompare(a.assessmentDate ?? ''))
        .slice(0, 3),
    [item.appraisals],
  );

  const glanceMetrics = useMemo((): TopMetric[] => {
    const vesselName = current?.vessel?.vesselName ?? current?.vesselName ?? null;
    const coeDate = current?.coeDate ?? current?.latestCoeDate ?? current?.extCoeDate ?? null;
    const coeDays = daysUntil(coeDate);
    const nextMedical = [...(item.medicals ?? [])]
      .filter((m) => m.nextDueDate)
      .sort((a, b) => (a.nextDueDate ?? '').localeCompare(b.nextDueDate ?? ''))[0];
    const medicalDays = daysUntil(nextMedical?.nextDueDate);
    const wage = formatWage(current);
    const wageRank = current?.rank?.abrv || current?.rank?.name || item.rank?.abrv || item.rank?.name;
    const intention = current?.intentionSignOffDate ?? null;
    const intentionDays = daysUntil(intention);

    return [
      {
        id: 'vessel',
        label: 'Current vessel',
        value: vesselName || '—',
        hint: current?.status === 'ONB' ? 'On board' : current ? 'Off board' : undefined,
        tone: current?.status === 'ONB' ? 'ok' : 'default',
      },
      {
        id: 'coe',
        label: 'COE',
        value: coeDate ? formatDate(coeDate) : '—',
        hint: daysHint(coeDays),
        tone: coeDays == null ? 'default' : coeDays < 0 ? 'danger' : coeDays <= 30 ? 'warning' : 'default',
      },
      {
        id: 'certs',
        label: 'Cert health',
        value:
          certStats.total === 0
            ? '—'
            : `${certStats.expired} expired · ${certStats.expiring} ≤90d`,
        hint: `${certStats.total} total`,
        tone:
          certStats.expired > 0 ? 'danger' : certStats.expiring > 0 ? 'warning' : certStats.total > 0 ? 'ok' : 'default',
      },
      {
        id: 'medical',
        label: 'Medical next due',
        value: nextMedical?.nextDueDate ? formatDate(nextMedical.nextDueDate) : '—',
        hint:
          medicalDays == null
            ? nextMedical?.kind ?? undefined
            : `${daysHint(medicalDays)}${nextMedical?.kind ? ` · ${nextMedical.kind}` : ''}`,
        tone:
          medicalDays == null ? 'default' : medicalDays < 0 ? 'danger' : medicalDays <= 30 ? 'warning' : 'default',
      },
      {
        id: 'wage',
        label: 'Last wage',
        value: wage || '—',
        hint: wageRank ? `In rank ${wageRank}` : undefined,
      },
      {
        id: 'availability',
        label: 'Availability',
        value: item.availableFrom ? formatDate(item.availableFrom) : '—',
        hint: item.availableTo
          ? `To ${formatDate(item.availableTo)}`
          : item.availabilityStatus?.name ?? undefined,
      },
      {
        id: 'intention',
        label: 'Intention S/Off',
        value: intention ? formatDate(intention) : '—',
        hint: daysHint(intentionDays, 'past'),
        tone:
          intentionDays == null ? 'default' : intentionDays < 0 ? 'danger' : intentionDays <= 30 ? 'warning' : 'default',
      },
      {
        id: 'age',
        label: 'Age',
        value: (() => {
          const age = ageFromBirthDate(item.birthDate);
          return age != null ? String(age) : '—';
        })(),
        hint: item.nationality?.name ?? undefined,
      },
    ];
  }, [certStats, current, item.availabilityStatus?.name, item.availableFrom, item.availableTo, item.birthDate, item.medicals, item.nationality?.name, item.rank]);

  const peopleMetrics = useMemo((): TopMetric[] => {
    const appraisals = item.appraisals ?? [];
    const grades = appraisals
      .map((a) => a.employmentStatus?.sortOrder)
      .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
    const avg = grades.length > 0 ? grades.reduce((sum, n) => sum + n, 0) / grades.length : null;
    const last = [...appraisals].sort((a, b) =>
      (b.assessmentDate ?? '').localeCompare(a.assessmentDate ?? ''),
    )[0];

    return [
      {
        id: 'appraisalAvg',
        label: 'Appraisal avg.',
        value:
          avg != null
            ? avg.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
            : '—',
        hint: grades.length ? `${grades.length} appraisal${grades.length === 1 ? '' : 's'}` : undefined,
      },
      {
        id: 'lastAppraisal',
        label: 'Last appraisal',
        value: last?.assessmentDate ? formatDate(last.assessmentDate) : '—',
        hint: last?.employmentStatus?.name ?? last?.appraisalType ?? undefined,
      },
      {
        id: 'lastContacted',
        label: 'Last contacted',
        value: item.lastContactedDate ? formatDate(item.lastContactedDate) : '—',
      },
      {
        id: 'lastApplication',
        label: 'Last application',
        value: item.lastApplicationDate ? formatDate(item.lastApplicationDate) : '—',
      },
    ];
  }, [item.appraisals, item.lastApplicationDate, item.lastContactedDate]);

  const tenureMetrics = useMemo(
    (): TopMetric[] =>
      EXPERIENCE_METRICS.map((metric) => {
        const raw = item.experience?.[metric.key];
        return {
          id: metric.key,
          label: metric.label,
          value: typeof raw === 'number' ? metric.format(raw) : '—',
          hint: metric.key === 'timeOnboardThisTourMonths' ? 'Months' : 'Years',
        };
      }),
    [item.experience],
  );

  const primaryNok = item.nextOfKin?.[0];
  const fileLink = item.id > 0 ? moduleConfig.module.route.file(item.id) : null;

  const handleGenerateAiOverview = async () => {
    if (!item.id || item.id <= 0 || preview) return;
    setAiLoading(true);
    try {
      const result = await generateCrewAiOverview(item.id);
      setAiOverview(result);
      setAiPanelOpen(true);
      if (historyOpen) {
        setHistoryItems((prev) => [result, ...prev.filter((row) => row.id !== result.id)]);
        setHistorySelectedId(result.id);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not generate AI overview.');
    } finally {
      setAiLoading(false);
    }
  };

  const openHistoryModal = async () => {
    if (!item.id || item.id <= 0 || preview) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const rows = await getCrewAiOverviewHistory(item.id);
      setHistoryItems(rows);
      setHistorySelectedId(rows[0]?.id ?? aiOverview?.id ?? null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not load AI overview history.');
      setHistoryItems([]);
      setHistorySelectedId(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setHistoryOpen(false);
    setHistoryLoading(false);
  };

  const selectedHistory = historyItems.find((row) => row.id === historySelectedId) ?? null;

  return (
    <div className="space-y-4 pb-2">
      {/* AI overview */}
      {!preview && item.id > 0 ? (
        <AccordionItem
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn('text-sm font-semibold', colors.text)}>AI overview</span>
              {aiOverview ? (
                <Badge variant={readinessVariant(aiOverview.overview.readiness)}>
                  {aiOverview.overview.readiness.replace(/_/g, ' ')}
                </Badge>
              ) : null}
            </span>
          }
          description="Operational summary generated from this crew file"
          open={aiPanelOpen}
          onToggle={setAiPanelOpen}
          headerAction={
            <div className="flex flex-wrap items-center gap-1.5 pr-1">
              <Button type="button" variant="outline" size="sm" onClick={openHistoryModal}>
                <ClockIcon className="h-3.5 w-3.5" />
                History
              </Button>
              <Button
                type="button"
                variant={aiOverview ? 'outline' : 'accent'}
                size="sm"
                loading={aiLoading}
                onClick={handleGenerateAiOverview}
              >
                <RefreshIcon className="h-3.5 w-3.5" />
                {aiOverview ? 'Refresh' : 'Generate'}
              </Button>
            </div>
          }
          className={cn(colors.surface, 'shadow-sm rounded-xl')}
        >
          {aiHydrating && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading saved overview…</p>
          ) : aiLoading && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Analyzing crew file…</p>
          ) : aiOverview ? (
            <AiOverviewBody data={aiOverview} />
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>
              Generate an AI overview from identity, tenure, assignments, certificates, medicals, appraisals, and notes.
            </p>
          )}
        </AccordionItem>
      ) : null}

      <Modal isOpen={historyOpen} onClose={closeHistoryModal} size="2xl">
        <ModalHeader>
          <ModalTitle>AI overview history</ModalTitle>
        </ModalHeader>
        <ModalContent className={colors.headerBg}>
          {historyLoading ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading history…</p>
          ) : historyItems.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No saved overviews yet.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-[minmax(12rem,16rem)_1fr] min-h-[20rem]">
              <div className={cn('rounded-lg border overflow-hidden', colors.border, colors.surface)}>
                <ul className={cn('max-h-[28rem] overflow-y-auto divide-y', colors.divide)}>
                  {historyItems.map((row) => {
                    const active = row.id === historySelectedId;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={() => setHistorySelectedId(row.id)}
                          className={cn(
                            'w-full text-left px-3 py-2.5 transition-colors',
                            active ? colors.surfaceHover : colors.interactiveHover,
                          )}
                        >
                          <p className={cn('text-xs font-medium', colors.textMuted)}>
                            {formatGeneratedAt(row.generatedAt)}
                          </p>
                          <p className={cn('mt-0.5 text-sm font-medium truncate', colors.text)}>
                            {row.overview.headline || 'Overview'}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant={readinessVariant(row.overview.readiness)}>
                              {row.overview.readiness.replace(/_/g, ' ')}
                            </Badge>
                            {aiOverview?.id === row.id ? <Badge variant="info">Latest</Badge> : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className={cn('rounded-lg border p-4', colors.border, colors.surface)}>
                {selectedHistory ? (
                  <div className="space-y-3">
                    <p className={cn('text-xs', colors.textMuted)}>
                      {formatGeneratedAt(selectedHistory.generatedAt)} · {selectedHistory.model}
                    </p>
                    <AiOverviewBody data={selectedHistory} compactMeta />
                  </div>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>Select a generation to view.</p>
                )}
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={closeHistoryModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* At a glance — ops first */}
      <MetricsGroup
        title="At a glance"
        metrics={glanceMetrics}
        columnsClass="grid-cols-2 md:grid-cols-4 xl:grid-cols-8"
      />

      <div className="grid gap-4 xl:grid-cols-2">
        <MetricsGroup
          title="Tenure"
          metrics={tenureMetrics}
          columnsClass="grid-cols-2 md:grid-cols-3"
        />
        <MetricsGroup
          title="People & recruitment"
          metrics={peopleMetrics}
          columnsClass="grid-cols-2 md:grid-cols-4"
        />
      </div>

      {/* Detail panels */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel
          title="Current tour"
          subtitle="Active or most recent assignment"
          action={
            !preview && fileLink ? (
              <Link to={fileLink} className={cn('text-xs font-medium hover:underline', colors.textSecondary)}>
                Open file
              </Link>
            ) : undefined
          }
        >
          {current ? (
            <dl>
              <InfoRow label="Vessel" value={current.vessel?.vesselName ?? current.vesselName} />
              <InfoRow label="Rank" value={current.rank?.abrv || current.rank?.name} />
              <InfoRow label="Status" value={current.status === 'ONB' ? 'On board' : 'Off board'} />
              <InfoRow label="Sign on" value={formatDate(current.signOnDate ?? current.startDate)} />
              <InfoRow label="Sign off" value={formatDate(current.signOffDate ?? current.endDate)} />
              <InfoRow
                label="COE"
                value={formatDate(current.coeDate ?? current.latestCoeDate ?? current.extCoeDate)}
              />
              <InfoRow label="Intention" value={formatDate(current.intentionSignOffDate)} />
              <InfoRow label="Wage" value={formatWage(current) ?? undefined} />
              <InfoRow label="Manning agent" value={current.manningAgent?.name} />
            </dl>
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>No assignment records yet.</p>
          )}
        </Panel>

        <Panel
          title="Documents"
          subtitle="Attention items first"
          action={
            !preview && sortedCerts.length > 0 && fileLink ? (
              <Link to={fileLink} className={cn('text-xs font-medium hover:underline', colors.textSecondary)}>
                View all
              </Link>
            ) : undefined
          }
        >
          {sortedCerts.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No documents recorded.</p>
          ) : (
            <ul className="space-y-2">
              {sortedCerts.slice(0, 5).map((cert: CrewCertRow, index) => {
                const status = certStatus(cert.expiredDate);
                return (
                  <li
                    key={cert.id ?? index}
                    className={cn('rounded-lg border px-3 py-2', colors.border, colors.surfaceHover)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn('text-sm font-medium min-w-0 truncate', colors.text)}>
                        {cert.certCatalog?.name ?? 'Document'}
                      </p>
                      <Badge variant={status.variant} className="shrink-0">
                        {status.label}
                      </Badge>
                    </div>
                    {cert.certNumber ? (
                      <p className={cn('text-xs mt-1 font-mono', colors.textMuted)}>{cert.certNumber}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>

        <div className="flex flex-col gap-4 min-h-0">
          <Panel title="Personal & contact" className="flex-1">
            <dl>
              <InfoRow label="Birth" value={formatDate(item.birthDate)} />
              <InfoRow label="Birth place" value={item.birthPlace} />
              <InfoRow label="Sex" value={item.sexType?.name} />
              <InfoRow label="Marital" value={item.maritalStatus?.name} />
              <InfoRow label="Manning agent" value={item.manningAgent?.name} />
              <InfoRow label="Phone" value={item.telephone1 || item.mobile1} />
              <InfoRow label="Email" value={item.email1} />
              <InfoRow label="Country" value={item.country?.name} />
              <InfoRow label="Nearest port" value={item.nearestPort?.name} />
            </dl>
          </Panel>

          <Panel title="Next of kin">
            {primaryNok ? (
              <dl>
                <InfoRow label="Name" value={primaryNok.fullName} />
                <InfoRow label="Relation" value={primaryNok.relation?.name} />
                <InfoRow label="Mobile" value={primaryNok.mobile ?? primaryNok.phone} />
              </dl>
            ) : (
              <p className={cn('text-sm', colors.textMuted)}>No next of kin recorded.</p>
            )}
          </Panel>
        </div>
      </div>

      {(recentAppraisals.length > 0 || item.duties || item.availabilityNotes || item.crewNotes) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {recentAppraisals.length > 0 ? (
            <Panel title="Recent appraisals" subtitle="Latest performance records">
              <ul className="space-y-2">
                {recentAppraisals.map((row, index) => (
                  <li
                    key={row.id ?? index}
                    className={cn('rounded-lg border px-3 py-2', colors.border, colors.surfaceHover)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className={cn('text-sm font-medium truncate', colors.text)}>
                        {row.vessel?.vesselName ?? row.appraisalType ?? 'Appraisal'}
                      </p>
                      <span className={cn('text-xs shrink-0', colors.textMuted)}>
                        {formatDate(row.assessmentDate)}
                      </span>
                    </div>
                    <p className={cn('text-xs mt-1', colors.textMuted)}>
                      {[row.employmentStatus?.name, row.assessorName ? `Assessor: ${row.assessorName}` : null]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </p>
                  </li>
                ))}
              </ul>
            </Panel>
          ) : null}

          {(item.duties || item.availabilityNotes || item.crewNotes) && (
            <Panel title="Notes">
              <dl>
                <InfoRow label="Duties" value={item.duties} />
                <InfoRow label="Availability" value={item.availabilityNotes} />
                <InfoRow label="Crew notes" value={item.crewNotes} />
              </dl>
            </Panel>
          )}
        </div>
      )}
    </div>
  );
};

export default CrewDashboard;
